import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  Activity,
  Sparkles,
  Flame,
  Zap,
  ArrowRight,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import GreetingCard from '@/components/dashboard/widgets/GreetingCard';
import LegalNoticeBanner from '@/components/shared/LegalNoticeBanner';
import StatTile from '@/components/dashboard/widgets/StatTile';
import SectionCard from '@/components/dashboard/widgets/SectionCard';
import ProgressSummaryCard from '@/components/dashboard/widgets/ProgressSummaryCard';
import simulationService from '@/services/simulationService';
import sessionService from '@/services/sessionService';
import { useDocumentProcessingPoller } from '@/hooks/useDocumentProcessingPoller';
import type { SimulationResponse } from '@/types/simulation';
import type { SessionResponse, MyProgressStatsResponse } from '@/types/session';
import { formatDateShort } from '@/utils/formatDate';
import { toScore99 } from '@/utils/scoreFormat';

type DashboardData = {
  assignments: SimulationResponse[];
  recentSessions: SessionResponse[];
  stats: MyProgressStatsResponse | null;
};

export default function SalespersonDashboard() {
  const { t } = useTranslation();
  const { user, isTrial, isSuperUser } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isSuperUser) {
      // SU sólo ve el saludo en /dashboard. No requiere assignments/sessions/stats.
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.allSettled([
      simulationService.getMyAssignments(),
      sessionService.getMine(1, 5),
      sessionService.getMyStats(),
    ])
      .then(([assignments, sessions, stats]) => {
        if (cancelled) return;
        setData({
          assignments: assignments.status === 'fulfilled' ? assignments.value : [],
          recentSessions: sessions.status === 'fulfilled' ? sessions.value.items : [],
          stats: stats.status === 'fulfilled' ? stats.value : null,
        });
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isSuperUser]);

  if (isSuperUser) {
    return (
      <div className="animate-fade-in space-y-6">
        <GreetingCard firstName={user?.firstName} lastName={user?.lastName} />
      </div>
    );
  }

  useDocumentProcessingPoller(data?.assignments, (updated) => {
    setData((prev) =>
      prev
        ? { ...prev, assignments: prev.assignments.map((s) => (s.id === updated.id ? updated : s)) }
        : prev,
    );
  });

  const pendingAssignments =
    data?.assignments.filter((s) => !data.recentSessions.some((r) => r.simulationId === s.id)) ?? [];
  const completedCount = data?.stats?.totalSessions ?? 0;
  const avgScore = data?.stats?.averageScore ?? 0;
  const lastSession = data?.recentSessions[0];
  const streak = data?.stats?.improvementStreak ?? 0;

  const weeklyGoal = 3;
  const weeklyDone = completedCount;
  const goalPct = Math.min(100, Math.round((weeklyDone / weeklyGoal) * 100));

  return (
    <div className="animate-fade-in">
      {error && (
        <div role="alert" className="mb-5 rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {t('dashboard.error', 'No se pudieron cargar los datos. Intenta recargar.')}
        </div>
      )}

      <LegalNoticeBanner
        storageKey="disclaimer-salesperson-dismissed"
        messageKey="legal.salespersonTransparency"
        learnMoreLink="/data-privacy"
      />

      {isTrial && completedCount === 0 && !loading && (
        <div className="mb-5 flex flex-col gap-3 rounded-xl border border-primary/30 bg-primary/5 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-heading text-foreground">
              {t('dashboard.trial.welcomeTitle', { defaultValue: 'Bienvenido a theRolePlayLab' })}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('dashboard.trial.welcomeDescription', {
                defaultValue: 'Empieza con una simulación rápida para sentir cómo es entrenar con un comprador IA.',
              })}
            </p>
          </div>
          <Link
            to="/simulations"
            className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors no-underline"
          >
            <Zap className="size-4" />
            {t('dashboard.trial.startCta', { defaultValue: 'Empezar Quick Practice' })}
            <ArrowRight className="size-4" />
          </Link>
        </div>
      )}

      <div className="grid gap-5 md:grid-cols-2">
        {/* LEFT COLUMN */}
        <div className="space-y-5">
          <GreetingCard
            firstName={user?.firstName}
            lastName={user?.lastName}
            subtitle={t('dashboard.salesperson.subtitle', 'Tu panel de entrenamiento')}
          />

          <ProgressSummaryCard stats={data?.stats ?? null} loading={loading} />
        </div>

        {/* RIGHT COLUMN */}
        <div className="space-y-5">
          <SectionCard title={t('dashboard.goal.title', 'Objetivo semanal')}>
            {loading ? (
              <Skeleton className="h-6 w-full" />
            ) : (
              <>
                <div className="mb-3 flex items-baseline justify-between">
                  <span className="text-4xl font-semibold tabular-nums text-foreground">{goalPct}%</span>
                  <span className="text-sm text-muted-foreground">
                    {weeklyDone} / {weeklyGoal} {t('dashboard.goal.sessions', 'sesiones')}
                  </span>
                </div>
                <Progress value={goalPct} />
                {streak > 0 && (
                  <p className="mt-3 flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                    <Flame className="size-3.5" /> {streak} {t('dashboard.goal.streak', 'sesiones mejorando')}
                  </p>
                )}
              </>
            )}
          </SectionCard>

          <SectionCard
            title={t('dashboard.actions.title', 'Acciones importantes')}
            contentClassName="grid grid-cols-2 xl:grid-cols-4 gap-3"
          >
            <StatTile
              icon={AlertTriangle}
              tone="warning"
              label={t('dashboard.actions.pending', 'Pendientes')}
              value={loading ? '—' : pendingAssignments.length}
              to="/simulations"
            />
            <StatTile
              icon={Activity}
              tone="info"
              label={t('dashboard.actions.completed', 'Completadas')}
              value={loading ? '—' : completedCount}
              to="/progress"
            />
            <StatTile
              icon={Sparkles}
              tone="success"
              label={t('dashboard.actions.avgScore', 'Score')}
              value={loading ? '—' : avgScore ? toScore99(avgScore) : '—'}
              to="/progress"
            />
            <StatTile
              icon={Flame}
              tone="warning"
              label={t('dashboard.actions.streak', 'Racha')}
              value={loading ? '—' : streak > 0 ? `${streak} 🔥` : '—'}
              hint={streak > 0 ? t('dashboard.actions.streakHint', 'sesiones mejorando') : undefined}
              to="/progress"
            />
          </SectionCard>

          <SectionCard title={t('dashboard.lastReport.title', 'Último reporte')}>
            {loading ? (
              <Skeleton className="h-20 w-full" />
            ) : !lastSession ? (
              <div className="flex flex-col items-center justify-center gap-2 py-4 text-center">
                <Sparkles className="size-5 text-muted-foreground/50" />
                <p className="text-sm text-muted-foreground">
                  {t('dashboard.lastReport.empty', 'Aún no tienes sesiones')}
                </p>
              </div>
            ) : (
              <div className="flex items-end justify-between gap-3">
                <div>
                  <div className="text-4xl font-semibold tabular-nums text-primary">
                    {lastSession.report ? toScore99(lastSession.report.overallScore) : '—'}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {formatDateShort(lastSession.completedAt ?? lastSession.startedAt)}
                  </div>
                </div>
                <Button asChild variant="outline" size="sm">
                  <Link to={`/simulation/${lastSession.simulationId ?? lastSession.id}`}>
                    {t('dashboard.lastReport.cta', 'Ver reporte')}
                  </Link>
                </Button>
              </div>
            )}
          </SectionCard>

        </div>
      </div>
    </div>
  );
}
